/**
 * The firm's read of one client's answers (docs/43 §3.2, §3.3, §3.5, §7; slice 5,
 * crossmarc/steward-app#2699), in two shapes:
 *
 *   1. {@link AppcoreFirmViewSchema}: what appcore's `GET /w/{ws}/advisor-clients/{clientId}/answers
 *      ?form=snapshot|onboarding` answers (appcore docs/06 §33, `readClientRecordFirmView`).
 *      The façade parses it and never forwards it.
 *   2. {@link FirmClientAnswersResponseSchema}: what the façade sends the SPA. It differs from (1)
 *      in four ways, each a decision rather than a convenience:
 *        - `by` and `via` are gone. `via` becomes the derived mark `updatedDuringOnboarding`, and
 *          `by` becomes `enteredBy.name`, a display name resolved server-side (or `null` when it
 *          cannot be). A raw user id never reaches the browser (§3.3).
 *        - D18 (§3.5, Matt 2026-10-31): the per-question "answered, not shown" markers are sent
 *          only when `markers` is true (the client is invited, or staff-entry consent is recorded). Otherwise there are none, and
 *          `sectionsStarted` carries today's count and nothing finer.
 *        - `connected` and `documents` are the firm projections the intake screens already read
 *          (`IntakeFirmSnapshot`, `IntakeFirmDocument`), not the stored rows.
 *        - `identityProvided` carries the two SSN "Provided" flags, which are not answers
 *          (docs/43 I1) and so are not in appcore's view.
 *
 * Nothing in the cell types has a field that could hold a hidden value: a hidden answer is
 * `{ hidden: true }` in both shapes, so a mapping bug cannot serialize one by accident.
 */
import { z } from 'zod';
import { IntakeFirmDocumentSchema, IntakeFirmSnapshotSchema } from './intakeFirmView.js';
import { ULID_PATTERN } from './intakeMessage.js';
import { ONBOARDING_FORM_FIELDS, ONBOARDING_FORM_SECTIONS } from './onboardingForm.js';
import { ONBOARDING_TO_UNIFIED, placementOf, type UnifiedForm } from './unifiedQuestions.js';
import { STAFF_ENTRY_CONSENT_MAX_LENGTH } from './staffEntryConsent.js';

export const FIRM_CLIENT_ANSWERS_FORMS = ['snapshot', 'onboarding'] as const;
export const FirmClientAnswersFormSchema = z.enum(FIRM_CLIENT_ANSWERS_FORMS);

/** The 403 appcore answers an unassigned advisor (appcore `resolveClientIdentityAuthority`). */
export const CLIENT_IDENTITY_NOT_AUTHORIZED_CODE = 'CLIENT_IDENTITY_NOT_AUTHORIZED';

const AnswerValue = z.union([z.string(), z.boolean(), z.array(z.string())]);
const Instant = z.string().max(40);

// ── 1. appcore's view ──────────────────────────────────────────────────────────────────────

const AppcoreVisibleAnswer = z.object({
  value: AnswerValue,
  via: z.enum(['snapshot', 'onboarding', 'staff', 'connected']),
  at: Instant,
  by: z.string().regex(ULID_PATTERN).optional(),
});
const Hidden = z.object({ hidden: z.literal(true) });
const AppcoreAnswer = z.union([Hidden, AppcoreVisibleAnswer]);

export const AppcoreFirmViewSchema = z.object({
  _v: z.literal(1),
  form: FirmClientAnswersFormSchema,
  answersVersion: z.number().int().min(0),
  answers: z.record(z.string(), AppcoreAnswer),
  // A list whose every item is hidden arrives as the bare marker (appcore docs/06 §33, 2026-11-01):
  // no array, no ids, no count. A list with any visible item stays an array.
  lists: z.record(z.string(), z.union([
    Hidden,
    z.array(z.union([
      z.object({ id: z.string(), hidden: z.literal(true) }),
      z.object({ id: z.string(), fields: z.record(z.string(), AppcoreAnswer) }),
    ])),
  ])),
  requests: z.object({
    answerIds: z.array(z.unknown()),
    answerTexts: z.array(z.unknown()),
  }).passthrough().nullable(),
  connected: z.union([Hidden, z.object({ snapshot: z.unknown() }).passthrough()]).nullable(),
  documents: z.union([Hidden, z.array(z.unknown())]).nullable(),
  stamps: z.object({
    sharingConsentAt: Instant.nullable(),
    snapshotSentAt: Instant.nullable(),
    onboardingSentAt: Instant.nullable(),
  }),
  // appcore adds this on form=onboarding (null on snapshot), from whether a tax-id identity row is
  // stored for the party (appcore#952). Kept optional so an older appcore still parses; the façade
  // then reports both parties as "not provided" and never reads the legacy onboarding-details row.
  identityProvided: z.strictObject({ client: z.boolean(), spouse: z.boolean() }).nullable().optional(),
  // docs/43 §8.5, crossmarc/steward-app#2538: the recorded verbal consent for staff entry, as the
  // firm read exposes it (appcore docs/06 §34, read 2026-11-02 at fdf4157, unmerged: the whole stamp,
  // `{ at, recordedBy, method, textVersion, text }`; only these three are kept). Optional so an
  // appcore without it still parses; absent reads as "not recorded".
  staffEntryConsent: z.object({
    at: Instant,
    recordedBy: z.string().max(64),
    text: z.string().max(STAFF_ENTRY_CONSENT_MAX_LENGTH),
  }).nullable().optional(),
});
export type AppcoreFirmView = z.infer<typeof AppcoreFirmViewSchema>;

// ── 1b. appcore's list names (crossmarc/steward-app#2737) ──────────────────────────────────

/**
 * The two questions a client record's list name is made of, in order. appcore's
 * `GET /w/{ws}/client-record-names` names the SAME two ids (`CLIENT_RECORD_NAME_QUESTION_IDS` in
 * its `client-record-repository.ts`, read 2026-11-02 on appcore branch `fix/2737-list-names`), and
 * composes them with {@link recordDisplayName}'s rule. Nothing in this repo can read that copy;
 * the steward-types test pins these ids against the unified question set, so a rename here goes red.
 */
export const CLIENT_RECORD_NAME_QUESTION_IDS = ['aboutYou.firstName', 'aboutYou.lastName'] as const;

/** The list name's join: string parts only, one space, trimmed; empty is `null`. appcore composes with the same rule. */
export function recordDisplayName(parts: readonly unknown[]): string | null {
  const name = parts.map((p) => (typeof p === 'string' ? p : '')).join(' ').trim();
  return name === '' ? null : name;
}

/** Two name answers at the unified cap (120 each) and the space, with room. */
export const CLIENT_RECORD_DISPLAY_NAME_MAX = 300;

/** One item of appcore's list-names answer. A record the caller may not read is absent from the list. */
export const AppcoreClientRecordNameSchema = z.object({
  clientId: z.string().min(1).max(64),
  displayName: z.string().min(1).max(CLIENT_RECORD_DISPLAY_NAME_MAX).nullable(),
});
export type AppcoreClientRecordName = z.infer<typeof AppcoreClientRecordNameSchema>;
export const AppcoreClientRecordNamesSchema = z.object({ items: z.array(z.unknown()) });

// ── 2. the façade's answer to the SPA ──────────────────────────────────────────────────────

export const FirmAnswerCellSchema = z.union([
  z.strictObject({ hidden: z.literal(true) }),
  z.strictObject({
    value: AnswerValue,
    at: Instant,
    /** `via = onboarding` and `at` after the snapshot was sent (§3.3). Absent otherwise. */
    updatedDuringOnboarding: z.literal(true).optional(),
    /** `via = staff`. `name` is a resolved display name, `null` when it could not be resolved. */
    enteredBy: z.strictObject({ name: z.string().max(200).nullable() }).optional(),
  }),
]);
export type FirmAnswerCell = z.infer<typeof FirmAnswerCellSchema>;

export const FirmListItemSchema = z.union([
  z.strictObject({ id: z.string(), hidden: z.literal(true) }),
  z.strictObject({ id: z.string(), fields: z.record(z.string(), FirmAnswerCellSchema) }),
]);
export type FirmListItem = z.infer<typeof FirmListItemSchema>;

export const FirmClientAnswersResponseSchema = z.strictObject({
  _v: z.literal(1),
  clientId: z.string().regex(ULID_PATTERN),
  form: FirmClientAnswersFormSchema,
  answersVersion: z.number().int().min(0),
  /** D18: true when the client is invited or `staffEntryConsent` is recorded. When false there is no `{ hidden: true }` anywhere below. */
  markers: z.boolean(),
  /** D18: the count of steps holding at least one answer (shown or not). `null` when `markers` is true. */
  sectionsStarted: z.number().int().min(0).nullable(),
  answers: z.record(z.string(), FirmAnswerCellSchema),
  /** A whole list can be the bare `{ hidden: true }` marker (all its items hidden). */
  lists: z.record(z.string(), z.union([z.strictObject({ hidden: z.literal(true) }), z.array(FirmListItemSchema)])),
  /** Onboarding form only. Visible without consent (decision 6). */
  requests: z.strictObject({ answerIds: z.array(z.string()), answerTexts: z.array(z.string()) }).nullable(),
  /** Snapshot form only. `{ hidden: true }` is sent only when `markers` is true. */
  connected: z.union([Hidden.strict(), IntakeFirmSnapshotSchema]).nullable(),
  /** Snapshot form only. Same rule as `connected`. */
  documents: z.union([Hidden.strict(), z.array(IntakeFirmDocumentSchema)]).nullable(),
  stamps: z.strictObject({
    sharingConsentAt: Instant.nullable(),
    snapshotSentAt: Instant.nullable(),
    onboardingSentAt: Instant.nullable(),
  }),
  /** Onboarding form only: the server-owned SSN "Provided" flags (docs/43 I1: the SSN is not an answer). */
  identityProvided: z.strictObject({ client: z.boolean(), spouse: z.boolean() }).nullable(),
  /** The newest `at` among the answers shown, or `null`. Drives "Last saved". */
  updatedAt: Instant.nullable(),
  /**
   * The recorded verbal consent for staff entry (docs/43 §8.5), or `null`. `recordedByName` is a
   * display name resolved server-side from the firm roster, never the recorder's user id.
   * Optional in the type for the same reason as `onboardingUpdatedAt`.
   */
  staffEntryConsent: z.strictObject({
    at: Instant,
    recordedByName: z.string().max(200).nullable(),
    text: z.string().max(STAFF_ENTRY_CONSENT_MAX_LENGTH),
  }).nullable().optional(),
  /**
   * The newest `at` among the VISIBLE answers whose origin (`via`) is the onboarding form, or
   * `null`. `updatedAt` spans every origin, because since docs/43 the snapshot and the onboarding
   * form share one record; this is the only field that says the CLIENT has been working in the
   * onboarding form (crossmarc/steward-app#2744). Optional in the type because the SPA does not
   * parse this schema at runtime (it is type and test only): an API deployed before the SPA
   * simply omits the field, and the adapter reads absent as `null`, the safe direction (no
   * "is filling this in").
   */
  onboardingUpdatedAt: Instant.nullable().optional(),
});
export type FirmClientAnswersResponse = z.infer<typeof FirmClientAnswersResponseSchema>;

export const isHiddenCell = (c: { hidden?: true } | object): c is { hidden: true } => 'hidden' in c;

// ── Shared reshaping (the façade's setup path and the SPA both use these) ──────────────────

/** The step a question belongs to on `form`, or undefined for an unplaced id. */
export function firmAnswerStep(id: string, form: UnifiedForm): string | undefined {
  return placementOf(id.split('#')[0]!)?.[form]?.step;
}

const UNIFIED_TO_ONBOARDING: ReadonlyMap<string, string> = new Map(
  Object.entries(ONBOARDING_TO_UNIFIED).filter(([k]) => k !== 'children_names' && k !== 'children_dateOfBirths').map(([k, v]) => [v, k]),
);

/**
 * The onboarding form's old flat answer bag, rebuilt from the VISIBLE cells of an onboarding
 * read, plus the set of legacy keys whose cell is hidden and the cell metadata per key. The Onboarding tab's
 * model and the setup plan both read this bag, so neither changes its shape (§9 minimal change).
 * Children are the two parallel arrays the old form kept, by item order.
 */
export interface OnboardingBag {
  attributes: Record<string, unknown>;
  hidden: ReadonlySet<string>;
  marks: ReadonlyMap<string, FirmAnswerMark>;
}
export interface FirmAnswerMark { enteredBy?: { name: string | null }; updatedDuringOnboarding?: true; /** The answer's `at`, for the mark's date. */ at?: string }

export function onboardingBagOf(view: Pick<FirmClientAnswersResponse, 'answers' | 'lists' | 'requests' | 'stamps' | 'identityProvided'>): OnboardingBag {
  const attributes: Record<string, unknown> = {};
  const hidden = new Set<string>();
  const marks = new Map<string, FirmAnswerMark>();
  for (const [id, cell] of Object.entries(view.answers)) {
    const key = UNIFIED_TO_ONBOARDING.get(id);
    if (key === undefined) continue;
    if (isHiddenCell(cell)) { hidden.add(key); continue; }
    attributes[key] = cell.value;
    if (cell.enteredBy) marks.set(key, { enteredBy: cell.enteredBy, at: cell.at });
  }
  const kidsRaw = view.lists['spousePartner.children'];
  if (kidsRaw && isHiddenCell(kidsRaw)) { hidden.add('children_names'); hidden.add('children_dateOfBirths'); }
  const kids = kidsRaw && !isHiddenCell(kidsRaw) ? kidsRaw : [];
  const names: string[] = [];
  const dobs: string[] = [];
  for (const item of kids) {
    if (isHiddenCell(item)) { hidden.add('children_names'); continue; }
    const n = item.fields['spousePartner.children[].name'];
    const d = item.fields['spousePartner.children[].dateOfBirth'];
    names.push(n && !isHiddenCell(n) && typeof n.value === 'string' ? n.value : '');
    dobs.push(d && !isHiddenCell(d) && typeof d.value === 'string' ? d.value : '');
    if (n && isHiddenCell(n)) hidden.add('children_names');
    if (d && isHiddenCell(d)) hidden.add('children_dateOfBirths');
  }
  if (names.length > 0) { attributes['children_names'] = names; attributes['children_dateOfBirths'] = dobs; }
  if (view.requests) {
    attributes['requests_answerIds'] = view.requests.answerIds;
    attributes['requests_answerTexts'] = view.requests.answerTexts;
  }
  if (view.stamps.onboardingSentAt) attributes['sentAt'] = view.stamps.onboardingSentAt;
  return { attributes, hidden, marks };
}

/** Every onboarding key a view could fill (for the census test that the inverse map is total). */
export const ONBOARDING_ANSWER_KEYS: readonly string[] = ONBOARDING_FORM_SECTIONS.flatMap((s) => ONBOARDING_FORM_FIELDS[s].map((f) => f.key));

/**
 * The snapshot form's nested submission, rebuilt from the VISIBLE cells of a snapshot read, for
 * the screens that already render an `IntakeSubmission` (`intakeDocument`) and for the setup
 * plan. Dotted ids become nested objects; a list becomes an array of its items' fields with the
 * item id as `rowId`. A hidden cell contributes nothing, so a hidden value cannot reach the
 * result by any path. The caller validates the result against the schema it needs.
 */
export function snapshotSubmissionOf(view: Pick<FirmClientAnswersResponse, 'answers' | 'lists'>): Record<string, unknown> {
  const root: Record<string, unknown> = {};
  const put = (target: Record<string, unknown>, path: string, value: unknown): void => {
    const parts = path.split('.');
    // An id comes from the pack manifest, so none is reserved today; refuse the three names that
    // would write onto a prototype rather than rely on that.
    if (parts.some((p) => p === '__proto__' || p === 'prototype' || p === 'constructor')) return;
    let at = target;
    for (const p of parts.slice(0, -1)) {
      const next = at[p];
      if (typeof next !== 'object' || next === null || Array.isArray(next)) at[p] = {};
      at = at[p] as Record<string, unknown>;
    }
    at[parts[parts.length - 1]!] = value;
  };
  for (const [id, cell] of Object.entries(view.answers)) {
    if (!isHiddenCell(cell)) put(root, id, cell.value);
  }
  for (const [listId, items] of Object.entries(view.lists)) {
    if (isHiddenCell(items)) continue;
    const rows: Record<string, unknown>[] = [];
    for (const item of items) {
      if (isHiddenCell(item)) continue;
      const row: Record<string, unknown> = { rowId: item.id };
      for (const [fieldId, cell] of Object.entries(item.fields)) {
        if (isHiddenCell(cell)) continue;
        const name = fieldId.slice(fieldId.indexOf('[].') + 3);
        row[name] = cell.value;
      }
      rows.push(row);
    }
    put(root, listId, rows);
  }
  return root;
}
